import React, { useCallback, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  PencilSquare,
  PlusLg,
  Search,
  Trash3,
  BoxSeam,
  Grid,
  List,
  Phone,
} from "react-bootstrap-icons";
import Drawer from "../../components/Common/Drawer";
import TooltipWrapper from "../../components/Common/TooltipWrapper";
import { useNavigate } from "react-router-dom";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import ConfirmModal from "../../components/Common/ConfirmModal";
import {
  deleteProducts,
  getProductsList,
} from "../../services/productServices";
import { useTranslation } from "react-i18next";
import DesktopTableSkeleton from "../../components/Common/DesktopTableSkeleton";
import FlexibleSkeleton from "../../components/Common/FlexibleSkeleton";
import CONFIG from "../../config";
import { useSelector } from "react-redux";
import { notify } from "../../components/Common/Alerts";
import { formatCurrencyINR } from "../../utils/function";
import { getCustomerAllList } from "../../services/customerServices";
import moment from "moment";
import { MapPin } from "lucide-react";

const Customers = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  let auth = useSelector((x) => x.auth.value);

  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [params, setParams] = useState({
    limit: CONFIG.DATA_LIMIT,
    page: 1,
    search: "",
    is_reload: true,
    store_id: auth.active_store_id,
  });
  const [customerList, setCustomerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecord, setTotalRecord] = useState(0);
  const [deleteShow, setDeleteShow] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [btnDeleteLoading, setBtnDeleteLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
  });
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"

  const currentPage = params.page;
  const totalPages = Math.ceil(totalRecord / params.limit);
  const startIndex = (currentPage - 1) * params.limit;
  const endIndex = startIndex + params.limit;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleParams = (e, type) => {
    let paramsObj = { ...params };

    if (type === "length") {
      paramsObj.limit = Number(e.target.value);
      paramsObj.page = 1;
    } else if (type === "pagination") {
      paramsObj.page = e;
    }

    setParams(paramsObj);
  };

  const handleSearch = () => {
    setParams((prev) => ({
      ...prev,
      ...filters,
      page: 1,
      is_reload: true,
    }));
    setShowOffcanvas(false);
  };

  const handleReset = () => {
    setFilters({
      search: "",
    });
    setParams({
      limit: 20,
      page: 1,
      search: "",
      is_reload: true,
    });
    setHasMore(true);
    setLoadingPage(false);
    setShowOffcanvas(false);
  };

  const handleDeleteClose = () => {
    setDeleteShow(false);
  };

  const handleDeleteShow = (e, item) => {
    setDeleteId(item);
    setDeleteShow(true);
  };

  const handleDeleteProducts = (e) => {
    e.preventDefault();
    setBtnDeleteLoading(true);

    let data = {
      uuid: deleteId.uuid,
      store_id: auth.active_store_id,
    };

    deleteProducts(data)
      .then(async (response) => {
        let decrypt = decryptData(response.data.data);
        notify.success(decrypt.message);

        getCustomerAllList((prevList = []) =>
          prevList.filter((role) => role.uuid !== deleteId.uuid),
        );
        setDeleteShow(false);
        setBtnDeleteLoading(false);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
        setBtnDeleteLoading(false);
      });
  };

  const fetchProductList = useCallback(() => {
    if (params.page === 1) {
      setLoading(true); // full page loader
    } else {
      setLoadingPage(true); // pagination loader
    }

    getCustomerAllList(params)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const newData = decrypted.data || [];

        setCustomerList(newData);

        setTotalRecord(decrypted.total || 0);

        if (newData.length < params.limit) setHasMore(false);
      })
      .finally(() => {
        setLoading(false);
        setLoadingPage(false);
        setSkeletonLoading(false);
      });
  }, [params]);

  useEffect(() => {
    fetchProductList();
  }, [params]);

  useEffect(() => {
    if (!isMobile) setLoading(false);
    setSkeletonLoading(true);

    // Reset state
    // setUnitsList([]);
    setHasMore(true);

    setParams((prev) => ({
      ...prev,
      page: 1,
      is_reload: true,
    }));
  }, [isMobile]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    const isBottom = scrollTop + clientHeight >= scrollHeight - 50;

    if (isBottom && hasMore && !loadingPage) {
      setLoadingPage(true);

      setParams((prev) => ({
        ...prev,
        page: prev.page + 1,
        is_reload: false,
      }));
    }
  };

  const handleOpen = () => {
    setShowOffcanvas(true);
  };

  return (
    <>
      <div className="h-full flex flex-col animate-fade-up space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center px-2">
          <div>
            <h6 className="text-xl md:text-2xl font-black text-slate-800">
              {t("customers")}
            </h6>

            <p className="text-[10px] md:text-xs text-gray-400 font-black uppercase tracking-widest">
              {totalRecord} {t("customersOnboarded")}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              {" "}
              <button
                onClick={() => setViewMode("grid")}
                disabled={viewMode === "grid"}
                title={t("switchToGridView")}
                className={`p-2 transition flex items-center justify-center 
                ${
                  viewMode === "grid"
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-white text-slate-600 hover:bg-orange-100"
                }`}
              >
                <Grid size={16} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                disabled={viewMode === "list"}
                title={t("switchToListView")}
                className={`  p-2 transition flex items-center justify-center
                ${
                  viewMode === "list"
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-white text-slate-600 hover:bg-orange-100"
                }`}
              >
                <List size={16} />
              </button>
            </div>
            <button className="mobile-filter-btn" onClick={handleOpen}>
              <Search size={18} />
            </button>

            <button className="desktop-filter-btn" onClick={handleOpen}>
              <Search size={18} />
              <span>{t("filter")}</span>
            </button>
            {Number(auth.user.role_id) === CONFIG.ROLE_OWNER && (
              <button
                className="add-btn"
                onClick={() => navigate("/products-add")}
              >
                <PlusLg />
                <span className="hidden md:block">{t("addNew")}</span>
              </button>
            )}
          </div>
        </div>
        {viewMode === "grid" ? (
          <div
            className="flex-1 space-y-3 md:space-y-4 overflow-y-auto pb-4 px-2 md:px-6 scroll-smooth"
            onScroll={handleScroll}
            style={{ marginBottom: "88px" }}
          >
            <main className="p-4 md:p-8 max-w-[1400px] mx-auto">
              {skeletonLoading ? (
                <FlexibleSkeleton
                  count={params.limit}
                  showAvatar={false}
                  showName
                  showRole
                  showContact={false}
                  showButtons
                />
              ) : customerList?.length > 0 ? (
                /* Responsive Grid: 1 column on mobile, 2 on tablet, 3-4 on desktop */
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                  {customerList.map((item, index) => (
                    <div
                      key={index}
                      className="group bg-white dark:bg-[#0F172A] rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                    >
                      {/* Header */}
                      <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5 text-white">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-xl font-bold truncate">
                              {item.name || "Guest Customer"}
                            </h3>

                            <p className="text-orange-100 text-sm mt-1">
                              Customer ID #{item.id}
                            </p>
                          </div>

                          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                            {(item.name || "G").charAt(0).toUpperCase()}
                          </div>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="p-5 space-y-4">
                        <div className="flex items-center gap-3">
                          <Phone size={18} className="text-orange-500" />
                          <div>
                            <p className="text-xs text-slate-500">Mobile</p>
                            <p className="font-semibold">
                              +{item.country_code} {item.mobile}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3">
                          <MapPin size={18} className="text-red-500 mt-1" />
                          <div>
                            <p className="text-xs text-slate-500">Address</p>
                            <p className="text-sm">
                              {item.address || "Address not available"}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
                            <p className="text-xs text-slate-500">Pincode</p>
                            <p className="font-semibold">
                              {item.pincode || "--"}
                            </p>
                          </div>

                          <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
                            <p className="text-xs text-slate-500">Joined</p>
                            <p className="font-semibold">
                              {moment(item.created_at).format("DD MMM YYYY")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Empty State */
                <div className="flex flex-col items-center justify-center py-24 text-center max-w-sm mx-auto">
                  <div className="w-20 h-20 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mb-6">
                    <BoxSeam size={32} className="text-slate-300" />
                  </div>
                  <h3 className="text-xl font-black text-slate-800 dark:text-white leading-tight">
                    {t("noCustomerFound")}
                  </h3>
                  <p className="text-sm font-medium text-slate-500 mt-2 mb-8">
                    {t("tryAdjustingFilter")}
                  </p>
                  <button
                    onClick={handleReset}
                    className="w-full py-4 bg-[#E67E22] text-white rounded-[20px] font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-orange-500/20 active:scale-95 transition-all"
                  >
                    {t("refresh")}
                  </button>
                </div>
              )}

              {loadingPage && (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-3 border-slate-200 border-t-[#E67E22] rounded-full animate-spin"></div>
                </div>
              )}
            </main>
          </div>
        ) : (
          <div className="hidden md:flex bg-white dark:bg-slate-900 rounded-[40px] border border-slate-100 dark:border-slate-800 shadow-sm flex-1 flex-col overflow-hidden transition-colors min-h-0">
            <div className="overflow-auto flex-1 custom-scrollbar">
              <table className="w-full text-left border-separate border-spacing-0">
                <thead className="bg-brand-orange dark:bg-slate-900 sticky top-0 z-10">
                  <tr>
                    <th className="table-header-text first:rounded-tl-[40px]">
                      #
                    </th>
                    <th className="table-header-text">{t("customer")}</th>
                    <th className="table-header-text">{t("mobile")}</th>
                    <th className="table-header-text">{t("address")}</th>
                    <th className="table-header-text">{t("pincode")}</th>
                    <th className="table-header-text">{t("createdAt")}</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {loading ? (
                    <DesktopTableSkeleton
                      rows={customerList?.length || 5}
                      columns={7}
                    />
                  ) : customerList?.length > 0 ? (
                    customerList.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-orange-50 dark:hover:bg-slate-800 transition-colors"
                      >
                        <td className="table-header-body font-bold">
                          {item.index}
                        </td>

                        {/* Customer */}
                        <td className="table-header-body">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold">
                              {(item.name || "G").charAt(0).toUpperCase()}
                            </div>

                            <div>
                              <p className="font-semibold text-slate-800 dark:text-white">
                                {item.name || "Guest"}
                              </p>

                              <p className="text-xs text-slate-500">
                                ID #{item.id}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Mobile */}
                        <td className="table-header-body">
                          +{item.country_code} {item.mobile}
                        </td>

                        {/* Address */}
                        <td className="table-header-body max-w-xs">
                          <span className="line-clamp-2">
                            {item.address || "-"}
                          </span>
                        </td>

                        {/* Pincode */}
                        <td className="table-header-body">
                          {item.pincode || "-"}
                        </td>

                        {/* Created */}
                        <td className="table-header-body">
                          {moment(item.created_at).format("DD MMM YYYY")}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-10 text-slate-500"
                      >
                        {t("noCustomerFound")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {customerList?.length > 0 && (
              <div className="p-4 md:p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center rounded-b-[40px] transition-colors hidden md:flex">
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-4 md:mb-0">
                  {t("showing")}{" "}
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    {startIndex + 1}
                  </span>{" "}
                  {t("to")}{" "}
                  <span className="font-bold text-slate-700 dark:text-slate-200">
                    {Math.min(endIndex, totalRecord)}
                  </span>{" "}
                  {t("of")} {totalRecord}
                </p>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleParams(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
                  >
                    <ChevronLeft size={18} />
                  </button>

                  <div className="hidden md:flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => handleParams(page)}
                          className={`w-9 h-9 rounded-xl text-sm font-black transition-all ${
                            currentPage === page
                              ? "bg-brand-orange text-white shadow-lg shadow-orange-200"
                              : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                          }`}
                        >
                          {page}
                        </button>
                      ),
                    )}
                  </div>

                  <div className="md:hidden px-4">
                    <span className="text-sm font-black text-brand-orange">
                      {t("page")} {currentPage} {t("of")} {totalPages}
                    </span>
                  </div>

                  <button
                    onClick={() => handleParams(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-30 transition-all"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Drawer
        isOpen={showOffcanvas}
        title={t("filterStore")}
        onClose={() => setShowOffcanvas(false)}
        footer={
          <>
            <div className="flex gap-3 flex-row justify-end w-full">
              <button className="btn-filter-close" onClick={handleReset}>
                {t("reset")}
              </button>

              <button className="btn-submit-orange" onClick={handleSearch}>
                {t("applyFilters")}
              </button>
            </div>
          </>
        }
      >
        <div className="space-y-4 p-2">
          <div className="flex flex-col gap-2">
            <label htmlFor="search" className="input-label">
              {t("search")}
            </label>
            <input
              type="text"
              name="search"
              className="input-field"
              value={filters?.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
            />
          </div>
        </div>
      </Drawer>

      <ConfirmModal
        open={deleteShow}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteProducts}
        loading={btnDeleteLoading}
        titleKey={t("deleteProduct")}
        descriptionKey={t("deleteConfirmation", { name: deleteId?.name })}
        confirmText="Yes, Delete"
        icon={<Trash3 size={32} className="text-red-500" />}
      />
    </>
  );
};

export default Customers;
